import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useSectionTracking } from "./AnalyticsProvider";
import { useLanguage } from "./LanguageProvider";

export default function RaritySection() {
  const sectionRef = useSectionTracking("rarity");
  const { t } = useLanguage();
  const v = t.valuation;
  const animRef = useRef(null);
  const inView = useInView(animRef, { once: true, margin: "-80px" });

  return (
    <section id="valuation" ref={sectionRef} className="relative py-12 lg:py-20 overflow-hidden" data-testid="valuation-section">
      <div className="absolute inset-0">
        <img src="/media/photos/new_5.jpg" alt="" className="w-full h-full object-cover opacity-[0.06]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10" ref={animRef}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
          <p className="overline mb-4">{v.overline}</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F4F0EA] font-light tracking-tighter">
            {v.title} <span className="italic text-[#C5A059]">{v.title_accent}</span>
          </h2>
        </motion.div>

        {/* Appraised by Ringsrud — credibility through named authority */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.2 }}
          className="my-12 lg:my-20"
        >
          <p className="font-serif text-[10vw] sm:text-[8vw] lg:text-[5vw] font-light tracking-tighter text-[#F4F0EA] leading-none">
            Expert Appraisal
          </p>
          <p className="font-serif text-[10vw] sm:text-[8vw] lg:text-[5vw] font-light tracking-tighter text-[#C5A059]/40 leading-none mt-1">
            Gübelin Certified
          </p>
          <p className="text-base text-[#B8B3AA] mt-6 max-w-xl font-light leading-relaxed">
            Appraised by Ringsrud Gemology — a GIA Graduate Gemologist with over four decades in the Colombian emerald trade, 
            author of <em>Emeralds: A Passionate Guide</em>, and a recognised contributor to the field whose work has appeared 
            in GIA's peer-reviewed <em>Gems & Gemology</em> journal. Independently certified by Gübelin Gem Lab, Zurich. 
            Full documentation available to qualified parties upon request.
          </p>
        </motion.div>

        {/* Rarity Numbers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-[#C5A059]/15">
          {[
            { stat: v.rarity_1_stat, desc: v.rarity_1_desc, detail: v.rarity_1_detail },
            { stat: v.rarity_2_stat, desc: v.rarity_2_desc, detail: v.rarity_2_detail },
            { stat: v.rarity_3_stat, desc: v.rarity_3_desc, detail: v.rarity_3_detail },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.4 + i * 0.15 }}
              className="py-10 lg:py-14 border-b md:border-b-0 md:border-r border-[#C5A059]/10 last:border-r-0 last:border-b-0 pr-0 md:pr-8 lg:pr-12"
              data-testid={`rarity-card-${i}`}
            >
              <div className="font-serif text-5xl sm:text-6xl lg:text-7xl gold-gradient-text tracking-tighter leading-none mb-4">
                {item.stat}
              </div>
              <p className="text-base text-[#F4F0EA]/80 font-medium mb-3">{item.desc}</p>
              <p className="text-base text-[#B8B3AA]/75 leading-relaxed font-light">{item.detail}</p>
            </motion.div>
          ))}
        </div>

        {/* Colombian Emerald Auction Records */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-8 lg:mt-14"
        >
          <h3 className="font-serif text-2xl lg:text-3xl text-[#F4F0EA]/80 mb-3">The Company She Keeps</h3>
          <p className="text-base text-[#B8B3AA]/75 mb-8 font-light">The world's most legendary emeralds — and the collectors and institutions that secured them</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#C5A059]/10">
            {[
              { name: "Duke of Devonshire Emerald", weight: "1,383.93 ct", usd: "Priceless", year: "c. 1831", house: "Natural History Museum", lot: "London, Permanent Collection", note: "Uncut Muzo hexagonal crystal. Gifted by Emperor Pedro I of Brazil to the 6th Duke of Devonshire. Exhibited at the 1851 Great Exhibition. One of the most famous uncut emeralds in existence — and Bridgette's closest peer.", colour: "bg-[#C5A059]/[0.04]" },
              { name: "The Aga Khan Emerald", weight: "37.41 ct", usd: "~$8.83M USD", year: "Nov 2024", house: "Christie's Geneva", lot: "Lot 100, Magnificent Jewels", note: "Cartier platinum brooch commissioned by Prince Sadruddin Aga Khan for Nina Dyer, 1960. Colombian origin, no enhancement (SSEF & Gübelin certified). Guinness World Record for most expensive emerald at auction.", colour: "" },
              { name: "The Rockefeller Emerald", weight: "18.04 ct", usd: "$5.5M USD", year: "Jun 2017", house: "Christie's New York", lot: "Magnificent Jewels", note: "Acquired by John D. Rockefeller Jr., 1930. Remounted by Raymond Yard, 1948. AGL certified. Considered one of the finest Colombian emeralds ever to appear at public sale.", colour: "" },
              { name: "Elizabeth Taylor Bulgari", weight: "23.44 ct", usd: "$6.5M USD", year: "Dec 2011", house: "Christie's New York", lot: "The Collection of Elizabeth Taylor", note: "Colombian emerald pendant/brooch, a gift from Richard Burton. Part of the most valuable private jewellery collection ever auctioned ($115.9M total).", colour: "" },
              { name: "Patricia Emerald", weight: "632 ct", usd: "Priceless", year: "c. 1920", house: "American Museum of Natural History", lot: "New York, Permanent Collection", note: "Named after the patron saint of miners. One of the largest known uncut Colombian emeralds. Displayed at AMNH since 1953. A dihexagonal crystal — rare even among large specimens.", colour: "" },
            ].map((comp, i) => (
              <div key={i} className={`p-6 lg:p-7 ${comp.colour || "bg-[#1A1918]"}`} data-testid={`auction-record-${i}`}>
                <div className="font-serif text-2xl lg:text-3xl text-[#C5A059] mb-2">{comp.usd}</div>
                <h4 className="font-serif text-lg text-[#F4F0EA]/90 mb-4">{comp.name}</h4>
                <div className="space-y-2 text-base mb-4">
                  <div className="flex justify-between gap-3"><span className="text-[#B8B3AA]/70 shrink-0">Weight</span><span className="text-[#F4F0EA]/80 text-right">{comp.weight}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-[#B8B3AA]/70 shrink-0">Year</span><span className="text-[#F4F0EA]/70 text-right">{comp.year}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-[#B8B3AA]/70 shrink-0">Institution</span><span className="text-[#F4F0EA]/70 text-right">{comp.house}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-[#B8B3AA]/70 shrink-0">Context</span><span className="text-[#F4F0EA]/75 text-sm text-right">{comp.lot}</span></div>
                </div>
                <p className="text-sm text-[#B8B3AA]/70 leading-relaxed">{comp.note}</p>
              </div>
            ))}
          </div>

          {/* The exclusivity callout */}
          <div className="mt-8 p-6 border-l-2 border-[#C5A059]/30">
            <p className="text-base text-[#B8B3AA] leading-relaxed font-light">
              <span className="text-[#C5A059]">Why this matters:</span> The Duke of Devonshire sits behind glass in London's Natural History Museum. 
              The Patricia has been in New York's AMNH since 1953. The collectors who acquired the Aga Khan and the Rockefeller 
              owned something no one else on Earth could possess. Bridgette is <span className="text-[#F4F0EA]">the only raw Colombian emerald 
              in 150 years</span> to receive a Gübelin Premium Profile — and unlike the museum specimens, she is available to a private collector.
            </p>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-12 text-base text-[#B8B3AA]/70 italic max-w-2xl font-light leading-relaxed"
        >
          {v.rarity_footnote}
        </motion.p>
      </div>
    </section>
  );
}
